/** Central app state and the quality presets that drive the synthesizer. */

import type { MechanismSpec } from "../kinematics/types.js";
import type { P2 } from "../synthesis/curve.js";
import type { SynthResult } from "../synthesis/optimize.js";
import type { ProgressMessage } from "./protocol.js";
import { MECHANISM_PRESETS } from "./presets.js";

export type Mode = "idle" | "draw" | "synthesizing";
export type ThemeChoice = "auto" | "light" | "dark";
export type Quality = "fast" | "balanced" | "thorough";

export interface QualitySettings {
  restarts: number;
  itersPerRestart: number;
  polishIters: number;
  samples: number;
  refineSamples: number;
}

export const QUALITY: Record<Quality, QualitySettings> = {
  fast: {
    restarts: 4,
    itersPerRestart: 90,
    polishIters: 120,
    samples: 40,
    refineSamples: 160,
  },
  balanced: {
    restarts: 8,
    itersPerRestart: 150,
    polishIters: 240,
    samples: 48,
    refineSamples: 200,
  },
  thorough: {
    restarts: 16,
    itersPerRestart: 240,
    polishIters: 420,
    samples: 60,
    refineSamples: 260,
  },
};

export interface Pose {
  positions: P2[];
  coupler: P2;
  converged: boolean;
}

export interface State {
  spec: MechanismSpec;
  topology: "fourbar" | "stephenson3";
  target: P2[] | null;
  driverAngle: number;
  playing: boolean;
  /** radians per second */
  speed: number;
  mode: Mode;
  trace: P2[] | null;
  traceOk: boolean;
  convergedFraction: number;
  pose: Pose | null;
  synthRunning: boolean;
  progress: ProgressMessage | null;
  result: SynthResult | null;
  theme: ThemeChoice;
  showLabels: boolean;
  quality: Quality;
  allowScale: boolean;
  seed: number;
  /** bumped whenever `spec` changes so the render loop re-traces */
  specVersion: number;
}

export function createInitialState(): State {
  return {
    spec: MECHANISM_PRESETS[2].make(),
    topology: "fourbar",
    target: null,
    driverAngle: 0,
    playing: true,
    speed: 1.4,
    mode: "idle",
    trace: null,
    traceOk: false,
    convergedFraction: 0,
    pose: null,
    synthRunning: false,
    progress: null,
    result: null,
    theme: "auto",
    showLabels: true,
    quality: "balanced",
    allowScale: true,
    seed: 7,
    specVersion: 0,
  };
}
